/**
 * Fetches every ledger the Transaction History drawer shows, and merges them
 * into one sorted list of display rows.
 *
 * Lives here rather than in the component because it is entirely data: seven
 * React Query hooks plus the mapping from each response shape onto
 * `TransactionRow`. The card renders what this returns.
 *
 * Each underlying hook is `enabled` only for the filters that need it, so
 * switching filters does not fetch ledgers the user is not looking at.
 */

import {useMemo} from "react";
import {TimeFilter, Token} from "@devour/client";
import {useGetTransactionHistoryXP} from "@/hooks/useGetTransactionHistoryXP";
import {useGetTransactionHistoryGamingXP} from "@/hooks/useGetTransactionHistoryGamingXP";
import {useGetTransactionHistoryFuel} from "@/hooks/useGetTransactionHistoryFuel";
import {useGetTransactionHistoryFood} from "@/hooks/useGetTransactionHistoryFood";
import {useGetTransactionHistoryLootShop} from "@/hooks/useGetTransactionHistoryLootShop";
import {useGetTransactionHistoryLootShopCredit} from "@/hooks/useGetTransactionHistoryLootShopCredit";
import {useGetTransactionHistoryRedemptions} from "@/hooks/useGetTransactionHistoryRedemptions";
import {useGetUserLevel} from "@/hooks/useGetUserLevel";
import {
    TransactionFilter,
    TransactionRow,
    TransactionRowCategory,
} from "@/components/play/cards/transactionHistory/transactionHistoryTypes";
import {
    formatTransactionDescription,
    formatXPDescription,
} from "@/components/play/cards/transactionHistory/transactionHistoryFormat";

/** Newest first, capped — the list scrolls, it does not paginate. */
const MAX_ROWS = 50;

interface GetTransactionHistoryRowsResult {
    rows: Array<TransactionRow>;
    isLoading: boolean;
    /**
     * True when at least one ledger failed. The drawer merges several sources,
     * so a partial failure still renders whatever succeeded — but it must not
     * be shown as "No transactions found", which reads as "you have none".
     */
    isError: boolean;
}

export function useGetTransactionHistoryRows(
    fullToken: Token | undefined,
    userId: string | undefined,
    filter: TransactionFilter,
    timeFilter: TimeFilter,
): GetTransactionHistoryRowsResult {
    const {isLoading: levelLoading} = useGetUserLevel(fullToken, userId, !fullToken || !userId);

    const {data: xpData, isLoading: xpLoading, error: xpError} = useGetTransactionHistoryXP(fullToken, {
        timeFilter,
        enabled: filter === "all" || filter === "xp" || filter === "quests",
    });

    const {data: gamingXpData, isLoading: gamingXpLoading, error: gamingXpError} = useGetTransactionHistoryGamingXP(fullToken, {
        timeFilter,
        enabled: filter === "all" || filter === "gaming",
    });

    const {data: fuelData, isLoading: fuelLoading, error: fuelError} = useGetTransactionHistoryFuel(fullToken, {
        timeFilter,
        enabled: filter === "all" || filter === "fuel",
    });

    const {data: foodData, isLoading: foodLoading, error: foodError} = useGetTransactionHistoryFood(fullToken, {
        timeFilter,
        enabled: filter === "all" || filter === "orders",
    });

    const {data: lootshopData, isLoading: lootshopLoading, error: lootshopError} = useGetTransactionHistoryLootShop(fullToken, {
        timeFilter,
        enabled: filter === "all" || filter === "orders",
    });

    /*
     * LootShop CREDIT — store credit won or granted.
     *
     * Distinct from useGetTransactionHistoryLootShop above, which returns
     * LootShop ORDERS. Credit rewards had no endpoint and so appeared under no
     * filter: Fuel rewards show under "fuel", XP rewards under "xp", and a $1
     * wheel win paid in LootShop Credit showed nowhere at all. That is the gap
     * this fills, and it is what feeds the previously-dead "rewards" filter.
     */
    const {data: lootshopCreditData, isLoading: lootshopCreditLoading, error: lootshopCreditError} = useGetTransactionHistoryLootShopCredit(
        fullToken,
        {
            timeFilter,
            enabled: filter === "all" || filter === "rewards",
        },
    );

    const {data: redemptionData, isLoading: redemptionLoading, error: redemptionError} = useGetTransactionHistoryRedemptions(fullToken, {
        timeFilter: timeFilter as any,
        enabled: filter === "all" || filter === "lootbox",
    });

    const rows = useMemo<Array<TransactionRow>>(() => {
        const all: Array<TransactionRow> = [];

        if (xpData?.transactions && (filter === "all" || filter === "xp" || filter === "quests")) {
            xpData.transactions.forEach((t) => {
                const isTimeReward = t.subject?.includes("TIME_REWARD") || t.subject?.includes("TIME_QUEST_REWARD");
                const isGamingEvent = t.subject?.includes("OVERWOLF_EVENT_REWARD");
                /* A quest is anything quest/challenge shaped that is not already one of the two above. */
                const isQuest = !isGamingEvent && !isTimeReward &&
                    (t.subject?.includes("QUEST") || t.subject?.includes("CHALLENGE"));

                // "xp" shows every XP row; "quests" narrows to quest completions.
                if (!(filter === "all" || filter === "xp" || filter === "quests" && isQuest)) {
                    return;
                }

                let category: TransactionRowCategory = "xp";
                if (isTimeReward) {
                    category = "time";
                } else if (isGamingEvent) {
                    category = "gaming";
                } else if (isQuest) {
                    category = "quest";
                }

                all.push({
                    id: `xp-${t.id}`,
                    type: "xp",
                    category,
                    amount: t.amount,
                    transactionType: t.type,
                    subject: t.subject,
                    createdAt: t.createdAt,
                    // Legacy subjects lower-case "FUEL"; restore the brand casing.
                    description: formatXPDescription(t.subject).replace(/fuel/gi, "Fuel"),
                });
            });
        }

        if (foodData?.transactions && (filter === "all" || filter === "orders")) {
            foodData.transactions.forEach((t) => {
                all.push({
                    id: `food-order-${t.id}`,
                    type: "orders",
                    category: "orders",
                    amount: t.amount, // cents
                    amountFormatted: t.amountFormatted,
                    transactionType: "DEBIT", // Orders are always money out.
                    subject: t.description,
                    merchant: t.merchant,
                    itemCount: t.itemCount,
                    paymentMethod: t.paymentMethod,
                    fuelUsed: t.fuelUsed,
                    createdAt: t.createdAt,
                    description: t.description, // Already formatted server-side.
                });
            });
        }

        if (lootshopData?.transactions && (filter === "all" || filter === "orders")) {
            lootshopData.transactions.forEach((t) => {
                all.push({
                    id: `lootshop-order-${t.id}`,
                    type: "orders",
                    category: "orders",
                    amount: t.amount, // cents
                    amountFormatted: t.amountFormatted,
                    transactionType: "DEBIT",
                    subject: t.description,
                    itemCount: t.itemCount,
                    paymentMethod: t.paymentMethod,
                    createdAt: t.createdAt,
                    description: t.description,
                });
            });
        }

        /*
         * LootShop CREDIT movements — the reward the user actually asks about.
         *
         * Only REWARD_DISTRIBUTION entries are surfaced under "rewards"; a
         * MENU_ORDER_PAYMENT against credit is a purchase and already belongs to
         * the orders view, so including it here would double-count the same
         * money under two filters.
         */
        if (lootshopCreditData?.transactions && (filter === "all" || filter === "rewards")) {
            lootshopCreditData.transactions
                .filter((t) => t.subject === "REWARD_DISTRIBUTION")
                .forEach((t) => {
                    all.push({
                        id: `lootshop-credit-${t.id}`,
                        type: "rewards",
                        // Singular — getTransactionIcon switches on "reward", not "rewards".
                        category: "reward",
                        amount: t.amount,
                        amountFormatted: t.amountFormatted,
                        transactionType: t.type,
                        subject: t.subject,
                        createdAt: t.createdAt,
                        description: `${t.description} — $${Number(t.amount ?? 0).toFixed(2)} LootShop Credit`,
                    });
                });
        }

        if (redemptionData?.transactions && (filter === "all" || filter === "lootbox")) {
            redemptionData.transactions.forEach((t) => {
                all.push({
                    id: `lootbox-redemption-${t.id}`,
                    type: "lootbox",
                    category: "lootbox",
                    amount: t.prizeValue || 0,
                    transactionType: "CREDIT", // Redemptions are prizes received.
                    subject: `${t.lootboxName} - ${t.prizeName}`,
                    lootboxName: t.lootboxName,
                    prizeName: t.prizeName,
                    prizeType: t.prizeType,
                    prizeImage: t.prizeImage,
                    createdAt: t.burnedAt || t.createdAt,
                    description: t.description || `${t.lootboxName} - ${t.prizeName}`,
                });
            });
        }

        /*
         * Gaming XP is a SUBSET of the XP ledger, so it is only added under the
         * dedicated "gaming" filter — under "all" the XP block above already
         * carries these rows, and adding them again would duplicate every one.
         */
        if (gamingXpData?.transactions && filter === "gaming") {
            gamingXpData.transactions.forEach((t) => {
                const category: TransactionRowCategory =
                    t.subject?.includes("TIME_REWARD") || t.subject?.includes("TIME_QUEST_REWARD")
                        ? "time"
                        : "gaming";

                all.push({
                    id: `gaming-xp-${t.id}`,
                    type: "gaming",
                    category,
                    amount: t.amount,
                    transactionType: t.type,
                    subject: t.subject,
                    createdAt: t.createdAt,
                    description: t.description, // Server-formatted, includes game names.
                });
            });
        }

        if (fuelData?.transactions && (filter === "all" || filter === "fuel")) {
            fuelData.transactions.forEach((t) => {
                all.push({
                    id: `fuel-${t.id}`,
                    type: "fuel",
                    category: "fuel",
                    amount: t.fuelAmount,
                    fiatAmount: t.fiatAmount,
                    fiatAmountFormatted: t.fiatAmountFormatted,
                    transactionType: t.type,
                    subject: t.subject,
                    createdAt: t.createdAt,
                    description: formatTransactionDescription(t.subject || t.description).replace(/fuel/gi, "Fuel"),
                });
            });
        }

        /*
         * `createdAt` is optional on every generated history model, so a missing
         * one sorts to 0 (oldest) rather than producing NaN, which would make
         * the comparator inconsistent and scramble the whole list.
         */
        const at = (row: TransactionRow): number => row.createdAt ? new Date(row.createdAt).getTime() : 0;

        return all.sort((a, b) => at(b) - at(a)).slice(0, MAX_ROWS);
    }, [
        xpData,
        gamingXpData,
        fuelData,
        foodData,
        lootshopData,
        lootshopCreditData,
        redemptionData,
        filter,
    ]);

    return {
        rows,
        isError: Boolean(xpError ||
            gamingXpError ||
            fuelError ||
            foodError ||
            lootshopError ||
            lootshopCreditError ||
            redemptionError),
        isLoading: levelLoading ||
            xpLoading ||
            gamingXpLoading ||
            fuelLoading ||
            foodLoading ||
            lootshopLoading ||
            lootshopCreditLoading ||
            redemptionLoading,
    };
}
